import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ArrowLeft, AlertTriangle, Eye } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  useCreatePayPeriod,
  usePreviewPayroll,
  useCalculatePayroll,
  useFinalizePayrollRun,
  usePayrollSettings,
} from '@/hooks/use-payroll';
import type {
  PayrollAdjustment,
  PayrollPreview,
  PayrollRunWithReadiness,
} from '@/lib/types';
import { handleApiError } from '@/lib/api-client';
import { formatMoney } from '@/lib/currency';
import { getPresetDates, payWeekStartDayName, type PeriodPreset } from '@/lib/pay-period-presets';
import { rateTypeLabel } from '@/lib/payroll-rate-type';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import { JobBreakdownDisclosure, overtimeMethodLabel } from './job-breakdown-disclosure';

function describeError(err: unknown, fallback: string): string {
  const apiError = handleApiError(err);
  if (apiError.status === 409) {
    return `${apiError.message} Reopen the existing run from its detail page before recalculating.`;
  }
  return apiError.message || fallback;
}

export function NewPayrollRunPage() {
  const navigate = useNavigate();
  const orgCurrency = useOrgCurrency();
  const orgToday = useOrgToday();
  const { data: payrollSettings, isLoading: isLoadingPayrollSettings } = usePayrollSettings();
  const payWeekStartDay = payrollSettings?.pay_week_start_day;
  const createPayPeriod = useCreatePayPeriod();
  const previewPayroll = usePreviewPayroll();
  const calculatePayroll = useCalculatePayroll();
  const finalizeRun = useFinalizePayrollRun();

  const [preset, setPreset] = useState<PeriodPreset>('custom');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payPeriodId, setPayPeriodId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PayrollPreview | null>(null);
  const [calculatedRun, setCalculatedRun] = useState<PayrollRunWithReadiness | null>(null);
  const [error, setError] = useState('');

  const handlePresetChange = (value: PeriodPreset) => {
    setPreset(value);
    if (value !== 'custom') {
      const dates = getPresetDates(value, orgToday, payWeekStartDay);
      setStartDate(dates.start);
      setEndDate(dates.end);
      setPayDate(dates.payDate);
    }
  };

  const handlePreview = async () => {
    setError('');
    if (!startDate || !endDate || !payDate) {
      setError('Please fill in all date fields.');
      return;
    }

    try {
      // Backend uses get-or-create: if a PayPeriod with matching
      // (org, start_date, end_date) exists it is returned (and pay_date
      // updated if different). No unique-constraint errors on re-preview.
      const payPeriod = await createPayPeriod.mutateAsync({
        start_date: startDate,
        end_date: endDate,
        pay_date: payDate,
      });
      const periodId = payPeriod.id;
      setPayPeriodId(periodId);
      const preview = await previewPayroll.mutateAsync(periodId);
      setPreviewData(preview);
    } catch (err: unknown) {
      setError(describeError(err, 'Failed to preview payroll.'));
    }
  };

  const handleConfirmCalculate = async () => {
    if (!payPeriodId) return;
    setError('');
    try {
      const run = await calculatePayroll.mutateAsync(payPeriodId);
      setCalculatedRun(run);
      setPreviewData(null);
    } catch (err: unknown) {
      setError(describeError(err, 'Failed to calculate payroll.'));
    }
  };

  const handleBackToEdit = () => {
    setPreviewData(null);
  };

  const handleApprove = async () => {
    if (!calculatedRun) return;
    try {
      await finalizeRun.mutateAsync(calculatedRun.id);
      navigate(`/payroll/runs/${calculatedRun.id}`);
    } catch (err: unknown) {
      setError(describeError(err, 'Failed to finalize payroll run.'));
    }
  };

  const isPreviewing = createPayPeriod.isPending || previewPayroll.isPending;
  const isCalculating = calculatePayroll.isPending;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/payroll/runs')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">New Payroll Run</h1>
          <p className="text-gray-600 mt-1">Create a pay period and calculate payroll</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
      )}

      {/* Pay Period Form */}
      {!previewData && !calculatedRun && (
        <Card>
          <CardHeader>
            <CardTitle>Pay Period</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="period-preset">Period Preset</Label>
              <Select
                value={preset}
                onValueChange={(v) => handlePresetChange(v as PeriodPreset)}
                disabled={isLoadingPayrollSettings}
              >
                <SelectTrigger id="period-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="semi-monthly">Semi-Monthly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                {isLoadingPayrollSettings ? (
                  'Loading pay-week settings…'
                ) : (
                  <>
                    Pay week starts {payWeekStartDayName(payWeekStartDay)} — change in Payroll
                    Settings. Weekly and Biweekly propose the most recent complete pay week(s); you
                    can edit any date below.
                  </>
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="pay-date">Pay Date</Label>
                <Input
                  id="pay-date"
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                />
              </div>
            </div>
            <Button onClick={handlePreview} disabled={isPreviewing}>
              <Eye className="h-4 w-4 mr-2" />
              {isPreviewing ? 'Loading Preview...' : 'Preview Payroll'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Preview Results (dry-run — no records created) */}
      {previewData && !calculatedRun && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Payroll Preview — {startDate} to {endDate}</CardTitle>
            </CardHeader>
            <CardContent>
              {overtimeMethodLabel(previewData.overtime_rate_method) && (
                <p className="mb-3 text-sm font-medium">
                  Overtime method: {overtimeMethodLabel(previewData.overtime_rate_method)}
                </p>
              )}
              {previewData.warnings?.length > 0 && (
                <div role="status" className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  {previewData.warnings.map((warning, index) => (
                    <p key={index}>
                      {typeof warning === 'string'
                        ? warning
                        : `${String(warning.worker ?? 'Worker')}: missing rate on ${String(warning.date ?? 'date')} for ${String(warning.project_name ?? 'job')}`}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-sm text-gray-500 mb-4">
                This is a preview only — no payroll records have been created yet.
                Review the numbers below, then confirm to commit the payroll run.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 pr-4">Name</th>
                      <th className="pb-2 pr-4 text-right">Rate Type</th>
                      <th className="pb-2 pr-4 text-right">Hrs / Days</th>
                      <th className="pb-2 pr-4 text-right">Reg Pay</th>
                      <th className="pb-2 pr-4 text-right">OT Pay</th>
                      <th className="pb-2 pr-4 text-right">Per Diem</th>
                      <th className="pb-2 pr-4 text-right">Mileage</th>
                      <th className="pb-2 pr-4 text-right">Expenses</th>
                      <th className="pb-2 pr-4 text-right font-semibold">Total Reimb.</th>
                      <th className="pb-2 pr-4 text-right font-semibold">Gross Pay</th>
                      <th className="pb-2 text-right font-semibold">Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.line_items.map((item) => {
                      const lineGross = Number(item.gross_pay);
                      const lineReimb = Number(item.total_reimbursements);
                      return (
                        <tr key={`${item.person_id}-${item.rate_type}`} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-4">
                            <p className="font-medium">
                              {item.person_name ?? item.tech_name ?? 'Unknown'}{' '}
                              <span className="text-xs text-gray-500">{rateTypeLabel(item.rate_type)}</span>
                            </p>
                            <JobBreakdownDisclosure rows={item.job_breakdown ?? []} currency={orgCurrency} />
                          </td>
                          <td className="py-2 pr-4 text-right">{rateTypeLabel(item.rate_type)}</td>
                          <td className="py-2 pr-4 text-right">
                            {item.rate_type === 'daily'
                              ? `${item.work_days} days`
                              : `${item.regular_hours} reg / ${item.overtime_hours} OT`}
                          </td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.regular_pay, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.overtime_pay, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.per_diem_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.mileage_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.expense_reimbursement_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(lineReimb, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right font-semibold">{formatMoney(lineGross, orgCurrency)}</td>
                          <td className="py-2 text-right font-semibold">{formatMoney(lineGross + lineReimb, orgCurrency)}</td>
                        </tr>
                      );
                    })}
                    {(previewData.pending_adjustments ?? []).map((adjustment) => {
                      const delta = Number(adjustment.delta);
                      const value = (field: PayrollAdjustment['field']) =>
                        adjustment.field === field ? delta : 0;
                      const totalReimb =
                        value('per_diem_total') +
                        value('mileage_total') +
                        value('expense_reimbursement_total');
                      const grossPay = value('regular_pay') + value('overtime_pay');
                      return (
                        <tr key={`adjustment-${adjustment.id}`} className="border-b bg-amber-50">
                          <td className="py-2 pr-4 font-medium">
                            {`Adjustment to Run #${adjustment.original_run} (${adjustment.original_run_pay_period_name}): ${adjustment.reason}`}
                          </td>
                          <td className="py-2 pr-4 text-right">Adjustment</td>
                          <td className="py-2 pr-4 text-right">
                            {adjustment.field === 'regular_hours' ? `${delta} reg` : ''}
                            {adjustment.field === 'overtime_hours' ? `${delta} OT` : ''}
                          </td>
                          <td className="py-2 pr-4 text-right">{formatMoney(value('regular_pay'), orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(value('overtime_pay'), orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(value('per_diem_total'), orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(value('mileage_total'), orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(value('expense_reimbursement_total'), orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(totalReimb, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right font-semibold">{formatMoney(grossPay, orgCurrency)}</td>
                          <td className="py-2 text-right font-semibold">{formatMoney(grossPay + totalReimb, orgCurrency)}</td>
                        </tr>
                      );
                    })}
                    {previewData.line_items.length === 0 && (
                      <tr>
                        <td colSpan={11} className="py-4 text-center text-gray-400">
                          No payroll data for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-gray-50">
                      <td className="py-2 pr-4">TOTALS</td>
                      <td className="py-2 pr-4 text-right" colSpan={2}></td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_regular_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_overtime_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_per_diem, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_mileage_reimbursement, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_expense_reimbursement, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(previewData.total_reimbursements, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right font-semibold">
                        {formatMoney(
                          Number(previewData.total_regular_pay) + Number(previewData.total_overtime_pay),
                          orgCurrency
                        )}
                      </td>
                      <td className="py-2 text-right font-semibold">{formatMoney(previewData.grand_total, orgCurrency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button onClick={handleConfirmCalculate} disabled={isCalculating}>
              <Calculator className="h-4 w-4 mr-2" />
              {isCalculating ? 'Calculating...' : 'Confirm & Calculate'}
            </Button>
            <Button variant="outline" onClick={handleBackToEdit}>
              Back to Edit
            </Button>
          </div>
        </>
      )}

      {/* Committed Results */}
      {calculatedRun && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Payroll Run — {calculatedRun.pay_period_name}</CardTitle>
            </CardHeader>
            <CardContent>
              {overtimeMethodLabel(calculatedRun.overtime_rate_method) && (
                <p className="mb-3 text-sm font-medium">
                  Overtime method: {overtimeMethodLabel(calculatedRun.overtime_rate_method)}
                </p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 pr-4">Name</th>
                      <th className="pb-2 pr-4 text-right">Reg Hrs</th>
                      <th className="pb-2 pr-4 text-right">OT Hrs</th>
                      <th className="pb-2 pr-4 text-right">Reg Pay</th>
                      <th className="pb-2 pr-4 text-right">OT Pay</th>
                      <th className="pb-2 pr-4 text-right">Per Diem</th>
                      <th className="pb-2 pr-4 text-right">Mileage</th>
                      <th className="pb-2 pr-4 text-right">Expenses</th>
                      <th className="pb-2 pr-4 text-right font-semibold">Total Reimb.</th>
                      <th className="pb-2 pr-4 text-right font-semibold">Gross Pay</th>
                      <th className="pb-2 text-right font-semibold">Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedRun.line_items.map((item) => {
                      const lineGross = Number(item.gross_pay);
                      const lineReimb = Number(item.total_reimbursements);
                      return (
                        <tr key={item.id} className="border-b hover:bg-gray-50">
                          <td className="py-2 pr-4">
                            <p className="font-medium">
                              {item.person_name ?? item.tech_name ?? 'Unknown'}{' '}
                              <span className="text-xs text-gray-500">{rateTypeLabel(item.rate_type)}</span>
                            </p>
                            <JobBreakdownDisclosure rows={item.job_breakdown ?? []} currency={orgCurrency} />
                          </td>
                          <td className="py-2 pr-4 text-right">
                            {item.rate_type === 'daily'
                              ? `${Number(item.work_days).toFixed(0)} days`
                              : item.regular_hours}
                          </td>
                          <td className="py-2 pr-4 text-right">{item.overtime_hours}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.regular_pay, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.overtime_pay, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.per_diem_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.mileage_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(item.expense_reimbursement_total, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right">{formatMoney(lineReimb, orgCurrency)}</td>
                          <td className="py-2 pr-4 text-right font-semibold">{formatMoney(lineGross, orgCurrency)}</td>
                          <td className="py-2 text-right font-semibold">{formatMoney(lineGross + lineReimb, orgCurrency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-semibold bg-gray-50">
                      <td className="py-2 pr-4">TOTALS</td>
                      <td className="py-2 pr-4 text-right" colSpan={2}></td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_regular_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_overtime_pay, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_per_diem, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_mileage_reimbursement, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_expense_reimbursement, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right">{formatMoney(calculatedRun.total_reimbursements, orgCurrency)}</td>
                      <td className="py-2 pr-4 text-right font-semibold">{formatMoney(calculatedRun.gross_pay, orgCurrency)}</td>
                      <td className="py-2 text-right font-semibold">{formatMoney(calculatedRun.grand_total, orgCurrency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* S17: Readiness Summary */}
          {calculatedRun.readiness_summary && (
            (calculatedRun.readiness_summary.draft_timesheet_count > 0 ||
             calculatedRun.readiness_summary.pending_expense_count > 0 ||
             calculatedRun.readiness_summary.unreconciled_project_count > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="h-5 w-5" />
                    Readiness Warnings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-sm">
                    {calculatedRun.readiness_summary.draft_timesheet_count > 0 && (
                      <li>
                        {calculatedRun.readiness_summary.draft_timesheet_count} draft timesheet(s)
                        still in the period
                      </li>
                    )}
                    {calculatedRun.readiness_summary.pending_expense_count > 0 && (
                      <li>
                        {calculatedRun.readiness_summary.pending_expense_count} pending expense(s)
                        not yet approved
                      </li>
                    )}
                    {calculatedRun.readiness_summary.unreconciled_project_count > 0 && (
                      <li>
                        {calculatedRun.readiness_summary.unreconciled_project_count} project(s) with
                        unreconciled hours
                        <ul className="ml-4 mt-1">
                          {calculatedRun.readiness_summary.unreconciled_projects.map((p) => (
                            <li key={p.project_id}>
                              {p.project_name} ({p.pending_count} pending row(s))
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}
                  </ul>
                </CardContent>
              </Card>
            )
          )}

          <div className="flex gap-3">
            <Button onClick={handleApprove} disabled={finalizeRun.isPending}>
              {finalizeRun.isPending ? 'Finalizing...' : 'Finalize Payroll'}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/payroll/runs/${calculatedRun.id}`)}
            >
              View Details
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
